import React, { useCallback, useContext, useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';

import userService from 'services/userService';
import { APP_MOBILE, SUA_PAGES } from 'utils/app_constants';
import { getSearchedUsers, hasValue } from 'utils/helper_functions';
import Fetcher from 'components/Fetcher';
import ActiveUserTabContext from 'context/ActiveUserTabContext';
import classNames from 'classnames';
import ProductIcon from 'components/uiHelpers/ProductIcon';
import ProductFilter from './ProductFilter';
import ErrorWithImage from 'components/Error/Error';
import style from './UserSearchPanel.module.css';
import { STORAGE_KEYS, STORAGE_TYPES } from '../../../utils/app_constants';
import { storageRead } from '../../../utils/storageUtils';
import { TmoButtons as TmoButton } from '@tmobile/tmo-bps-syncup-web-component-lib';

/**
 * UesrSearchResults Component
 *
 * Responsibilities:
 *  - Trigger user search via Fetcher
 *  - Render user cards (Legacy + TLIFE flows)
 *  - Apply filtering
 *  - Handle TLIFE pagination
 */
function UesrSearchResults({ userSearchTerm }) {
  /** ------------------ STATE ------------------ **/

  // Main list of all fetched users (before filtering)
  const [userSearchResults, setUserSearchResults] = useState([]);

  // Final list after filtering (shown on screen)
  const [filteredUserList, setFilteredUserList] = useState([]);

  // API error
  const [error, setError] = useState(null);

  // TLIFE pagination
  const [pageData, setPageData] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);

  // Selected tab context (user selection)
  const { setSelectedTab, selectedTab } = useContext(ActiveUserTabContext);
  const { userId, product } = { ...selectedTab };

  // Determine search type from localStorage
  const sessionSearchType = storageRead({
    key: STORAGE_KEYS.SEARCH_TYPE,
    storageType: STORAGE_TYPES.LOCAL
  });

  /** ---------------------------------------------
   * RESET RESULTS whenever:
   *  - search term changes
   *  - search type changes
   ----------------------------------------------*/
  useEffect(() => {
    if (sessionSearchType || userSearchTerm) {
      setCurrentPage(0);
      setUserSearchResults([]);
      setFilteredUserList([]);
    }
  }, [sessionSearchType, userSearchTerm]);

  /** ---------------------------------------------
   * Pagination handler (TLIFE only)
   ----------------------------------------------*/
  const updatePageNumber = page => {
    setCurrentPage(page + 1);
  };

  /** ---------------------------------------------
   * Fetcher Action Callback
   * Determines which API to call:
   *  - LEGACY
   *  - TLIFE (paged)
   ----------------------------------------------*/
  const userActionCallback = useCallback(() => {
    if (sessionSearchType === 'LEGACY') {
      // Fresh search
      setUserSearchResults([]);
      setFilteredUserList([]);
      setCurrentPage(0);

      return Promise.allSettled([
        userService.getUsersBySearch({
          q: userSearchTerm,
          syncUpAppName: APP_MOBILE.DRIVE
        })
      ]);
    }

    // TLIFE search
    if (sessionSearchType === 'TLIFE') {
      return Promise.allSettled([
        userService.getTlifeUsersBySearch({
          q: userSearchTerm,
          syncUpAppName: [
            APP_MOBILE.TRACKER,
            APP_MOBILE.WATCH,
            APP_MOBILE.DRIVE
          ],
          page: currentPage
        })
      ]);
    }

    return [];
  }, [userSearchTerm, sessionSearchType, currentPage]);

  /** ---------------------------------------------
   * TLIFE helper: Flatten user + product objects
   * (No logic change, only comments/safety)
   ----------------------------------------------*/
  const getTlifeSearchedUsers = dataVal => {
    const datas =
      dataVal?.map(usr => ({
        ...usr.userProfile,
        ...usr.userApplicationProfile,
        flag: 'tlife'
      })) || [];

    let groupedObjects = [];
    let newArray = [];

    datas.forEach(usrTlife => {
      // Build product-specific obj combinations
      groupedObjects.push(
        usrTlife.tracker ? { ...usrTlife.tracker, ...usrTlife } : '',
        usrTlife.kidswatch ? { ...usrTlife.kidswatch, ...usrTlife } : '',
        usrTlife.phoneLocation
          ? { ...usrTlife.phoneLocation, ...usrTlife }
          : '',
        usrTlife.drive ? { ...usrTlife.drive, ...usrTlife } : ''
      );

      // Remove empties
      newArray = groupedObjects.filter(o => Object.keys(o).length !== 0);
    });

    return newArray;
  };

  /** ---------------------------------------------
   * Utility: Remove duplicate objects
   * (kept same method to preserve behavior)
   ----------------------------------------------*/
  const uniqueById = items => {
    const uniq = [];
    return items.filter(obj => {
      const s = JSON.stringify(obj);
      if (!uniq.includes(s)) {
        uniq.push(s);
        return true;
      }
      return false;
    });
  };

  /** ---------------------------------------------
   * Fetcher onLoad handler
   * Normalizes data across LEGACY + TLIFE
   ----------------------------------------------*/
  const handleLoad = useCallback(
    data => {
      setError(null);

      let result = [];
      let totalFailures = 0;

      data?.forEach(e => {
        if (sessionSearchType === 'LEGACY') {
          if (e.status === 'fulfilled' && hasValue(e.value)) {
            result = [...result, ...e.value];
          }
        }

        if (sessionSearchType === 'TLIFE') {
          if (e.status === 'fulfilled' && hasValue(e.value)) {
            const { pageDetails, pagedUserProfileDetails } = e.value;

            // Pagination details
            if (pageDetails) {
              setPageData(pageDetails);
              setCurrentPage(pageDetails.currentPage);
            }

            // Extract TLIFE page results
            if (pagedUserProfileDetails) {
              const mapped = pagedUserProfileDetails.map(u => ({ ...u }));
              result = [...result, ...mapped];
            }
          }
        }

        if (e.status === 'rejected') totalFailures++;
      });

      // If all API calls failed
      if (data.length === totalFailures) {
        setError(data[0].reason);
      }

      // Normalize Legacy/TLife format
      const normalizedUsers = getSearchedUsers(result);

      /** ---- TLIFE Flow ---- **/
      if (sessionSearchType === 'TLIFE') {
        let accumulated = [];

        normalizedUsers.forEach(usr => {
          const flattened = getTlifeSearchedUsers([{ ...usr }]);

          for (const obj of flattened) {
            accumulated.push(obj);

            // Update main result list
            setUserSearchResults(prev => uniqueById([...prev, ...accumulated]));

            // Update filtered list
            setFilteredUserList(prev => uniqueById([...prev, ...accumulated]));
          }
        });
      }

      /** ---- Legacy Flow ---- **/
      if (sessionSearchType === 'LEGACY') {
        setUserSearchResults(normalizedUsers);
        setFilteredUserList([...normalizedUsers]);
      }
    },
    [sessionSearchType]
  );

  /** ---------------------------------------------
   * Filter callback from <ProductFilter />
   ----------------------------------------------*/
  const handleOnFilter = useCallback(list => {
    setFilteredUserList(list);
  }, []);

  /** ---------------------------------------------
   * Card click handler (update selected tab)
   ----------------------------------------------*/
  const handleClick = user => {
    const pageUrl = SUA_PAGES.USER.path.replace(':userId', user.userId);

    setSelectedTab({
      users: user,
      name: user.firstName,
      userId: user.userId,
      url: `${pageUrl}/things`,
      product: user.product
    });
  };

  const showPagination = pageData && pageData.lastPage > currentPage;

  /** ------------------ RENDER ------------------ **/

  return (
    <>
      {userSearchTerm ? (
        <>
          <ProductFilter
            onFilter={handleOnFilter}
            userList={userSearchResults}
          />

          <div className={style.user_result_container} id={style.scrollable}>
            <Fetcher
              action={userActionCallback}
              onLoad={handleLoad}
              render={() => (
                <>
                  {/* Error block */}
                  {error && (
                    <ErrorWithImage
                      heading="Failed to fetch"
                      message={error.toString()}
                    />
                  )}

                  {/* User list */}
                  {filteredUserList &&
                    filteredUserList.map(user => (
                      <div
                        key={`${user.userId}-${user.product}`}
                        className={classNames(
                          style.user_card,
                          user.userId === userId &&
                            user.product === product &&
                            style.card_selected
                        )}
                        onClick={() => handleClick(user)}
                      >
                        <div className={style.user_info}>
                          <div className={style.title}>
                            {user.firstName} {user.lastName}
                          </div>
                          <div className={style.sub_title}>{user.email}</div>
                        </div>

                        <div className={style.product_icon}>
                          <ProductIcon product={user.product} />
                        </div>
                      </div>
                    ))}
                </>
              )}
            />

            {/* TLIFE Pagination */}
            {showPagination &&
              sessionSearchType === 'TLIFE' &&
              pageData.lastPage > 0 && (
                <div
                  className={style.loadMore}
                  onClick={() => updatePageNumber(currentPage)}
                >
                  <TmoButton type="magenta_link" className={style.Load_button}>
                    Load More
                  </TmoButton>
                </div>
              )}
          </div>
        </>
      ) : (
        <div className={style.no_result}></div>
      )}
    </>
  );
}

export default withRouter(UesrSearchResults);
